import { useEffect, useState } from "react";

import { useI18n } from "@/shared/i18n/LanguageContext";

import * as aboutContentEn from "./aboutContent";
import * as aboutContentZh from "./aboutContent.zh";

const aboutSectionIds = aboutContentEn.aboutNavigation.map((item) => item.id);
const initialAboutSectionId = aboutSectionIds[0] ?? "";

const aboutPageCopy = {
  en: {
    layoutLabel: "About Catie",
    navLabel: "About",
    navAria: "About page sections",
    principlesAria: "Catie principles",
    playCatie: "Play Catie",
    readPaper: "Read paper",
    figureAria: "Catie sequence prediction figure",
    figureLabel: "Figure 1",
    figureTitle: "Sequence-to-move prediction",
    tokenInputAria: "Move-token input",
    moveHistory: "Move history",
    nextToken: "Next token",
    distributionAria: "Example next-move distribution",
    zeroSearchAria: "No-search constraints",
    resultsAria: "Catie research results",
    maiaEyebrow: "What is Maia?",
    maiaTitle: "Maia is the reference point. Catie is the sequence-model answer.",
    maiaBody:
      "Maia-style systems are important because they made human-move prediction visible. Catie asks a different product question: what happens when a chess model reads the whole game like language?",
    bottleneckEyebrow: "Tracking vs. Deciding",
    bottleneckTitle: "The hard part is not just choosing moves. It is remembering the board.",
    bottleneckBody:
      "The paper describes a dual-capability bottleneck. Low-rated games add strange positions that teach tracking. High-rated games add better decisions. Removing one side breaks the other.",
    bottleneckAria: "Tracking and decision quality bottleneck",
    practicalPlay: "practical play",
    resultsEyebrow: "Test results",
    resultsTitle: "Catie reached strong human-level bullet play without search.",
    resultsBody:
      "These numbers come from the March 2026 paper. The Maia comparison is native-input: Catie gets move history, Maia-style models get position input, so the input format is part of the result.",
    risksEyebrow: "Potential issues",
    risksTitle: "The interesting failures are part of the product.",
    risksBody:
      "Catie is not Stockfish with a friendlier mask. It has model-native failure modes, and exposing those boundaries is why this website matters.",
    sftEyebrow: "About SFT",
    sftTitle: "One base model can become many Catie personalities.",
    sftBody:
      "Supervised fine-tuning is how we separate the foundation from the players. The goal is a flexible family, not a single fixed checkpoint.",
    futureEyebrow: "Future development",
    futureTitle: "The next Catie should be easier to inspect, split, and explain.",
    storyEyebrow: "Dev story",
    storyTitle: "The research path was a chain of failures that became a design.",
    developersEyebrow: "Developers",
    developersTitle: "The people building Catie.",
  },
  zh: {
    layoutLabel: "关于 Catie",
    navLabel: "关于",
    navAria: "About 页面目录",
    principlesAria: "Catie 原则",
    playCatie: "和 Catie 对弈",
    readPaper: "阅读论文",
    figureAria: "Catie 序列预测示意图",
    figureLabel: "图 1",
    figureTitle: "从序列预测下一步",
    tokenInputAria: "走法 token 输入",
    moveHistory: "走法历史",
    nextToken: "下一个 token",
    distributionAria: "下一步候选分布示例",
    zeroSearchAria: "无搜索约束",
    resultsAria: "Catie 研究结果",
    maiaEyebrow: "Maia 是什么？",
    maiaTitle: "Maia 是参照系，Catie 是序列模型给出的另一种答案。",
    maiaBody:
      "Maia 风格系统很重要，因为它让“预测人类走法”这件事被看见。Catie 提出的是另一个产品问题：如果一个国际象棋模型像读语言一样读完整盘棋，会发生什么？",
    bottleneckEyebrow: "追踪 vs 决策",
    bottleneckTitle: "难点不只是选一步棋，而是一直记住棋盘。",
    bottleneckBody:
      "论文描述了一个双能力瓶颈。低 rating 对局带来奇怪局面，帮助模型学习追踪；高 rating 对局带来更好的决策。少掉任何一边，另一边都会受损。",
    bottleneckAria: "追踪能力与决策质量瓶颈",
    practicalPlay: "实战表现",
    resultsEyebrow: "测试结果",
    resultsTitle: "Catie 在不做搜索的情况下，达到了很强的人类子弹棋水平。",
    resultsBody:
      "这些数字来自 2026 年 3 月论文。和 Maia 的比较是原生输入比较：Catie 使用走法历史，Maia 风格模型使用局面输入，因此输入形式本身也是结果的一部分。",
    risksEyebrow: "潜在边界",
    risksTitle: "这些有趣的失败，也是产品的一部分。",
    risksBody:
      "Catie 不是换了外壳的 Stockfish。它有模型原生的失败模式，而把这些边界展示出来，正是这个网站存在的意义。",
    sftEyebrow: "关于 SFT",
    sftTitle: "一个基础模型，可以变成许多 Catie 棋手人格。",
    sftBody:
      "监督微调让我们把基础能力和具体棋手分开。目标不是一个固定 checkpoint，而是一个灵活的模型家族。",
    futureEyebrow: "未来开发",
    futureTitle: "下一代 Catie 应该更容易检查、拆分和解释。",
    storyEyebrow: "开发故事",
    storyTitle: "这条研究路径，是一串失败最终变成设计的过程。",
    developersEyebrow: "开发者",
    developersTitle: "正在构建 Catie 的人。",
  },
} as const;

export function AboutPage() {
  const { language } = useI18n();
  const content = language === "zh" ? aboutContentZh : aboutContentEn;
  const pageCopy = aboutPageCopy[language];
  const {
    aboutDevelopers,
    aboutHeroSignals,
    aboutFigureChecks,
    aboutMetrics,
    aboutNavigation,
    aboutPredictionBars,
    aboutSections,
    aboutHeroTokens,
    bottleneckCards,
    capabilityBars,
    devStory,
    futureItems,
    issueCards,
    maiaComparison,
    paperUrl,
    sftNotes,
  } = content;
  const [activeSectionId, setActiveSectionId] = useState(initialAboutSectionId);

  useEffect(() => {
    if (aboutSectionIds.length === 0) {
      return undefined;
    }

    let animationFrame = 0;

    const updateActiveSection = () => {
      animationFrame = 0;
      const probeLine = window.innerHeight * 0.36;
      let nextSectionId = initialAboutSectionId;

      for (const sectionId of aboutSectionIds) {
        const section = document.getElementById(sectionId);

        if (!section) {
          continue;
        }

        if (section.getBoundingClientRect().top <= probeLine) {
          nextSectionId = sectionId;
        }
      }

      const isAtPageEnd =
        window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 4;

      if (isAtPageEnd) {
        nextSectionId = aboutSectionIds[aboutSectionIds.length - 1] ?? nextSectionId;
      }

      setActiveSectionId((currentSectionId) =>
        currentSectionId === nextSectionId ? currentSectionId : nextSectionId,
      );
    };

    const requestUpdate = () => {
      if (animationFrame !== 0) {
        return;
      }

      animationFrame = window.requestAnimationFrame(updateActiveSection);
    };

    updateActiveSection();
    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate);

    return () => {
      if (animationFrame !== 0) {
        window.cancelAnimationFrame(animationFrame);
      }

      window.removeEventListener("scroll", requestUpdate);
      window.removeEventListener("resize", requestUpdate);
    };
  }, []);

  useEffect(() => {
    const scrollToHashSection = () => {
      const sectionId = window.location.hash.slice(1);

      if (!aboutSectionIds.some((candidateId) => candidateId === sectionId)) {
        return;
      }

      window.requestAnimationFrame(() => {
        document.getElementById(sectionId)?.scrollIntoView();
      });
    };

    scrollToHashSection();
    window.addEventListener("hashchange", scrollToHashSection);

    return () => {
      window.removeEventListener("hashchange", scrollToHashSection);
    };
  }, []);

  useEffect(() => {
    const activeLink = document.querySelector<HTMLElement>(
      `[data-about-nav-id="${activeSectionId}"]`,
    );

    activeLink?.scrollIntoView({ block: "nearest", inline: "center" });
  }, [activeSectionId]);

  return (
    <div className="page-stack about-page">
      <section className="about-layout" aria-label={pageCopy.layoutLabel}>
        <aside className="about-nav" aria-label={pageCopy.navAria}>
          <span className="about-nav__label">{pageCopy.navLabel}</span>
          {aboutNavigation.map((item) => (
            <a
              key={item.id}
              className={
                activeSectionId === item.id
                  ? "about-nav__link about-nav__link--active"
                  : "about-nav__link"
              }
              href={`#${item.id}`}
              data-about-nav-id={item.id}
              aria-current={activeSectionId === item.id ? "location" : undefined}
            >
              {item.label}
            </a>
          ))}
        </aside>

        <main className="about-main">
          <section className="about-hero" id="overview">
            <div className="about-hero__copy">
              <p className="section-intro__eyebrow">{aboutSections.hero.eyebrow}</p>
              <h1>{aboutSections.hero.title}</h1>
              <p>{aboutSections.hero.body}</p>

              <div className="about-principles" aria-label={pageCopy.principlesAria}>
                {aboutHeroSignals.map((signal) => (
                  <span key={signal}>{signal}</span>
                ))}
              </div>

              <div className="button-row">
                <a className="button-link button-link--primary" href="/play">
                  {pageCopy.playCatie}
                </a>
                <a
                  className="button-link button-link--secondary"
                  href={paperUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  {pageCopy.readPaper}
                </a>
              </div>
            </div>

            <aside className="about-paper-figure" aria-label={pageCopy.figureAria}>
              <div className="about-paper-figure__head">
                <span>{pageCopy.figureLabel}</span>
                <strong>{pageCopy.figureTitle}</strong>
              </div>

              <div className="about-token-sentence" aria-label={pageCopy.tokenInputAria}>
                {aboutHeroTokens.map((token) => (
                  <span key={token}>{token}</span>
                ))}
              </div>

              <div className="about-figure-model" aria-hidden="true">
                <span>{pageCopy.moveHistory}</span>
                <strong>Catie</strong>
                <span>{pageCopy.nextToken}</span>
              </div>

              <div className="about-candidate-bars" aria-label={pageCopy.distributionAria}>
                {aboutPredictionBars.map((candidate) => (
                  <div key={candidate.move} className="about-candidate-bar">
                    <div>
                      <strong>{candidate.move}</strong>
                      <span>{candidate.label}</span>
                    </div>
                    <i>
                      <b style={{ width: `${candidate.value}%` }} />
                    </i>
                  </div>
                ))}
              </div>

              <div className="about-zero-search" aria-label={pageCopy.zeroSearchAria}>
                {aboutFigureChecks.map((check) => (
                  <div key={check.label}>
                    <strong>{check.value}</strong>
                    <span>{check.label}</span>
                  </div>
                ))}
              </div>
            </aside>
          </section>

          <section className="about-evidence-strip" aria-label={pageCopy.resultsAria}>
            {aboutMetrics.map((metric) => (
              <article key={metric.label} className="about-evidence-card">
                <strong>{metric.value}</strong>
                <span>{metric.label}</span>
                <p>{metric.detail}</p>
              </article>
            ))}
          </section>

          <section className="about-section" id="maia">
            <div className="about-section__head">
              <span>{pageCopy.maiaEyebrow}</span>
              <h2>{pageCopy.maiaTitle}</h2>
              <p>{pageCopy.maiaBody}</p>
            </div>

            <div className="about-comparison-grid">
              {maiaComparison.map((item) => (
                <article key={item.title} className="about-comparison-card">
                  <span>{item.label}</span>
                  <h3>{item.title}</h3>
                  <ul>
                    {item.lines.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </section>

          <section className="about-section" id="bottleneck">
            <div className="about-section__head">
              <span>{pageCopy.bottleneckEyebrow}</span>
              <h2>{pageCopy.bottleneckTitle}</h2>
              <p>{pageCopy.bottleneckBody}</p>
            </div>

            <div className="about-bottleneck">
              {bottleneckCards.map((card) => (
                <article key={card.key}>
                  <strong>{card.key}</strong>
                  <h3>{card.title}</h3>
                  <p>{card.body}</p>
                </article>
              ))}
            </div>

            <div className="about-capability-figure" aria-label={pageCopy.bottleneckAria}>
              <div className="about-capability-figure__formula">
                <span>{pageCopy.practicalPlay}</span>
                <strong>{"P <= min(T, Q)"}</strong>
              </div>
              <div className="about-capability-bars">
                {capabilityBars.map((bar) => (
                  <article key={bar.label}>
                    <div>
                      <strong>{bar.label}</strong>
                      <span>{bar.detail}</span>
                    </div>
                    <i>
                      <b style={{ width: `${bar.value}%` }} />
                    </i>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section className="about-section" id="results">
            <div className="about-section__head">
              <span>{pageCopy.resultsEyebrow}</span>
              <h2>{pageCopy.resultsTitle}</h2>
              <p>{pageCopy.resultsBody}</p>
            </div>

            <div className="about-metric-grid">
              {aboutMetrics.map((metric) => (
                <article key={metric.label} className="about-metric-card">
                  <strong>{metric.value}</strong>
                  <span>{metric.label}</span>
                  <p>{metric.detail}</p>
                </article>
              ))}
            </div>
          </section>

          <section className="about-section" id="risks">
            <div className="about-section__head">
              <span>{pageCopy.risksEyebrow}</span>
              <h2>{pageCopy.risksTitle}</h2>
              <p>{pageCopy.risksBody}</p>
            </div>

            <div className="about-issue-grid">
              {issueCards.map((issue) => (
                <article key={issue.title}>
                  <h3>{issue.title}</h3>
                  <p>{issue.body}</p>
                </article>
              ))}
            </div>
          </section>

          <section className="about-section" id="sft">
            <div className="about-section__head">
              <span>{pageCopy.sftEyebrow}</span>
              <h2>{pageCopy.sftTitle}</h2>
              <p>{pageCopy.sftBody}</p>
            </div>

            <div className="about-sft-flow">
              {sftNotes.map((note) => (
                <article key={note.title}>
                  <h3>{note.title}</h3>
                  <p>{note.body}</p>
                </article>
              ))}
            </div>
          </section>

          <section className="about-section" id="future">
            <div className="about-section__head">
              <span>{pageCopy.futureEyebrow}</span>
              <h2>{pageCopy.futureTitle}</h2>
            </div>

            <ol className="about-roadmap">
              {futureItems.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ol>
          </section>

          <section className="about-section" id="story">
            <div className="about-section__head">
              <span>{pageCopy.storyEyebrow}</span>
              <h2>{pageCopy.storyTitle}</h2>
            </div>

            <div className="about-storyline">
              {devStory.map((story) => (
                <article key={story.step}>
                  <span>{story.step}</span>
                  <div>
                    <h3>{story.title}</h3>
                    <p>{story.body}</p>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section className="about-section" id="developers">
            <div className="about-section__head">
              <span>{pageCopy.developersEyebrow}</span>
              <h2>{pageCopy.developersTitle}</h2>
            </div>

            <div className="about-developer-grid">
              {aboutDevelopers.map((developer) => (
                <article key={developer.name} className="about-developer-card">
                  <div className="about-avatar" aria-hidden="true">
                    <span>{developer.initials}</span>
                  </div>
                  <div>
                    <h3>{developer.name}</h3>
                    <p>{developer.affiliation}</p>
                    <strong>{developer.role}</strong>
                  </div>
                </article>
              ))}
            </div>
          </section>
        </main>
      </section>
    </div>
  );
}
